"use client";

/**
 * Модалка «Аренда в 1 клик» — открывается по одноимённой кнопке на карточке
 * машины (CarCard, рендерится на главной и странице города). Клиент оставляет
 * ТОЛЬКО телефон. POST /api/v1/one-click → бэк шлёт в Telegram-чат города
 * (модель машины + город + телефон + хештег #аренда_в_один_клик). Без оплаты,
 * без перехода — быстрый лид на обратный звонок. Дизайн: фото машины баннером
 * сверху + название/город/цена/характеристики, как в карточке.
 */

import Img from '@/components/Img';
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import PhoneInput from "./PhoneInput";
import { oneClickLead } from "@/lib/api";
import { track } from "@/lib/metrika";
import { useLocale } from "@/lib/locale-context";
import { carSpecLabel } from "@/lib/car-spec-i18n";
import { useToast } from "@/components/ui/Toast";
import type { Car } from "./CarCard";

// Перевод названий цветов (из 1С, по-русски) для отображения на /en.
const COLOR_EN: Record<string, string> = {
  "белый": "White", "чёрный": "Black", "черный": "Black", "серый": "Gray",
  "серебристый": "Silver", "серебряный": "Silver", "красный": "Red", "синий": "Blue",
  "голубой": "Light blue", "зелёный": "Green", "зеленый": "Green", "жёлтый": "Yellow",
  "желтый": "Yellow", "оранжевый": "Orange", "коричневый": "Brown", "бежевый": "Beige",
  "золотой": "Gold", "золотистый": "Gold", "фиолетовый": "Purple",
};
const colorLabel = (n: string, locale: string) =>
  locale === "en" ? COLOR_EN[(n || "").trim().toLowerCase()] || n : n;

// Свотч цвета (как в InlineBookingPanel) — кружок по названию цвета.
const COLOR_HEX: Record<string, string> = {
  "белый": "#f5f5f5", "чёрный": "#1a1a1a", "черный": "#1a1a1a", "серый": "#9aa0a6",
  "серебристый": "#c8ccd0", "серебряный": "#c8ccd0", "красный": "#d52b1e", "синий": "#1e4fd5",
  "голубой": "#5aa9e6", "зелёный": "#2e9c50", "зеленый": "#2e9c50", "жёлтый": "#f5c518",
  "желтый": "#f5c518", "оранжевый": "#f08020", "коричневый": "#7a4f2b", "бежевый": "#d9c6a5",
  "золотой": "#caa84a", "золотистый": "#caa84a", "фиолетовый": "#7a3fd5",
};
const colorToHex = (n: string) => COLOR_HEX[(n || "").trim().toLowerCase()] || "#9aa0a6";

// Иконка класса (силуэт машины) по названию класса — как в InlineBookingPanel.
function classIcon(cls: string): string {
  const c = (cls || "").toLowerCase();
  if (/люкс|luxury/.test(c)) return "/icons/class/lux.svg";
  if (/бизнес/.test(c)) return "/icons/class/business.svg";
  if (/промо|эконом/.test(c)) return "/icons/class/promo.svg";
  if (/внедорож|suv|джип/.test(c)) return "/icons/class/jeep.svg";
  if (/кроссов/.test(c)) return "/icons/class/suv.svg";
  if (/минив[эе]н|minivan|van/.test(c)) return "/icons/class/van.svg";
  if (/кабриол|convertible/.test(c)) return "/icons/class/cab.svg";
  return "/icons/class/comfort.svg";
}

// Чекбокс согласия — как в блоке брони (2 штуки: политика + перс. данные).
function Check({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 text-[12.5px] leading-[1.45] text-text-muted">
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={checked}
        className={`mt-px flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border transition ${checked ? "border-primary bg-primary" : "border-[#C9D3E6] bg-white"}`}
      >
        {checked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </button>
      <span>{children}</span>
    </div>
  );
}

export default function OneClickModal({ car, onClose }: { car: Car; onClose: () => void }) {
  const { t, locale } = useLocale();
  const toast = useToast();
  const [mounted, setMounted] = useState(false);
  const [imgSrc, setImgSrc] = useState(car.image || "/images/catalog-car.png");
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState("+7");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [agreePrivacy, setAgreePrivacy] = useState(true);   // как в бронировании — по умолчанию отмечены
  const [agreePersonal, setAgreePersonal] = useState(true);

  useEffect(() => {
    setMounted(true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [onClose]);


  const submit = async () => {
    if (sending) return;
    if (phone.replace(/\D/g, "").length < 6) { toast.show(locale === "en" ? "Enter a valid phone number" : "Введите корректный номер телефона", "error"); return; }
    if (!agreePrivacy || !agreePersonal) { toast.show(t("consent.confirmRequired"), "error"); return; }
    setSending(true); setErr("");
    try {
      await oneClickLead({ station: car.station, city: car.city, car: car.title, phone: `${dial} ${phone}`.trim() });
      track("oneclick_submit", { city: car.station });
      toast.show(locale === "en" ? "Request sent" : "Заявка отправлена", "success");
      setDone(true);
    } catch {
      toast.show(t("form.sendError"), "error");
    } finally {
      setSending(false);
    }
  };

  // ТТХ (как в блоке брони): год/КПП/топливо/двигатель/места/багаж/привод/класс/
  // цвет — до 9 ячеек, компактная сетка 3×3 без карточек.
  const ic = (src: string) => <Img src={src} alt="" className="size-[13px] shrink-0 object-contain" />;
  const tiles: { icon: React.ReactNode; value: string }[] = [];
  if (car.year && car.year > 0) tiles.push({ icon: ic("/icons/spec-year.png"), value: String(car.year) });
  if (car.features?.transmission && car.features.transmission !== "—") tiles.push({ icon: ic("/icons/transmission.svg"), value: carSpecLabel(car.features.transmission, locale) });
  if (car.features?.fuel && car.features.fuel !== "—") tiles.push({ icon: ic("/icons/fuel.svg"), value: carSpecLabel(car.features.fuel, locale) });
  if (car.features?.drive && car.features.drive !== "—") tiles.push({ icon: ic("/icons/spec-engine.png"), value: carSpecLabel(car.features.drive, locale) });
  if (car.features?.seats && car.features.seats !== "—") tiles.push({ icon: ic("/icons/seat.svg"), value: carSpecLabel(car.features.seats, locale) });
  if (car.bagsNum && car.bagsNum > 0) tiles.push({ icon: ic("/icons/baggage.svg"), value: t("car.bags", { n: String(car.bagsNum) }) });
  if (car.driveUnit) tiles.push({ icon: ic("/icons/drive.svg"), value: carSpecLabel(car.driveUnit, locale) });
  if (car.carClass) tiles.push({ icon: <Img src={classIcon(car.carClass)} alt="" className="h-[13px] w-[19px] shrink-0 object-contain" />, value: car.carClass });
  if (car.color) tiles.push({ icon: <span className="inline-block size-3 shrink-0 rounded-full border border-[#D0D0D5]" style={{ background: colorToHex(car.color) }} />, value: colorLabel(car.color, locale) });

  if (!mounted) return null;

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[2100] flex items-center justify-center bg-[rgba(15,18,26,0.55)] p-4 backdrop-blur-[6px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[calc(100vh-32px)] w-full max-w-[720px] overflow-y-auto rounded-2xl bg-white shadow-[0_12px_48px_rgba(0,0,0,0.28)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="absolute right-3.5 top-3.5 z-10 flex size-9 cursor-pointer items-center justify-center rounded-full bg-white/85 text-text shadow-[0_2px_8px_rgba(0,0,0,0.12)] backdrop-blur transition hover:bg-white"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>

        {done ? (
          <div className="px-6 py-10 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-[#E8F6EE]">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#19a86a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
            <h2 className="text-[22px] font-semibold text-text">{t("oneclick.accepted")}</h2>
            <p className="mx-auto mt-2 max-w-[300px] text-[15px] leading-6 text-text-muted">
              {t("oneclick.acceptedText")}{car.title ? <> <span className="font-medium text-text">{car.title}</span></> : ""}.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full cursor-pointer rounded-xl bg-primary py-3.5 text-[16px] font-semibold text-white transition hover:bg-primary-hover"
            >
              {t("oneclick.gotIt")}
            </button>
          </div>
        ) : (
          <>
            {/* Карточка машины: фото СЛЕВА, инфо СПРАВА (название/город/цена +
                сетка ТТХ 3 в ряд) — как в блоке брони. */}
            <div className="flex flex-col gap-4 p-5 pb-0 sm:flex-row sm:p-6 sm:pb-0">
              {/* Фото: на мобиле баннером сверху, на десктопе слева */}
              <div className="w-full shrink-0 sm:w-[42%]">
                <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl bg-[#EEF2F9]">
                  <Image
                    src={imgSrc}
                    alt={car.title}
                    fill
                    sizes="260px"
                    className="object-cover"
                    onError={() => setImgSrc("/images/catalog-car.png")}
                  />
                </div>
              </div>

              {/* Инфо справа */}
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-[19px] font-semibold leading-tight text-text">{car.title}</h2>
                  {car.rating?.count > 0 && (
                    <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-[#B8791A]">
                      <svg width="13" height="13" viewBox="0 0 16 16" className="fill-[#ffb800]"><path d="M8 .7l2.49 5.05 5.57.8-4.03 3.93.95 5.55L8 13.4 2.02 16l.95-5.55L-1.06 6.55l5.57-.8L8 .7z" /></svg>
                      {car.rating.value.toFixed(1)}
                    </span>
                  )}
                </div>
                {car.city && (
                  <span className="mt-1 inline-flex items-center gap-1 text-[13px] text-text-muted">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0"><path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>
                    {car.city}{car.filial ? ` · ${locale === "en" ? "Office" : "Офис"} - ${car.filial}` : ""}
                  </span>
                )}
                {car.price?.current && (
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-[22px] font-bold leading-none text-[#d52b1e]">{car.price.current}</span>
                    {car.price.old && <span className="text-[14px] text-text-muted line-through">{car.price.old}</span>}
                    <span className="text-[12px] text-text-muted">{t("car.perDay")}</span>
                  </div>
                )}

                {tiles.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-x-2 gap-y-2">
                    {tiles.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 whitespace-nowrap text-[12px] leading-tight text-text">
                        {t.icon}
                        <span>{t.value}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 pt-4 sm:px-6">
              <div className="mb-4 border-t border-black/[0.07]" />

              <h3 className="text-[18px] font-semibold leading-tight text-text">{t("oneclick.title")}</h3>
              <p className="mt-1 text-[14px] leading-5 text-text-muted">
                {t("oneclick.subtitle")}
              </p>

              <label className="mt-4 mb-1.5 block text-[13px] font-medium text-text-muted">{t("oneclick.phoneLabel")}</label>
              <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setDial(d); setErr(""); }} height={52} />

              {/* Согласия — 2 чекбокса, как при бронировании */}
              <div className="mt-3.5 flex flex-col gap-2">
                <Check checked={agreePrivacy} onToggle={() => { setAgreePrivacy((v) => !v); setErr(""); }}>
                  {t("consent.privacyPrefix")}{" "}
                  <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("consent.privacy")}</a>
                </Check>
                <Check checked={agreePersonal} onToggle={() => { setAgreePersonal((v) => !v); setErr(""); }}>
                  {t("consent.personalPrefix")}{" "}
                  <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("consent.personalData")}</a>
                </Check>
              </div>

              {err && <p className="mt-3 text-[13px] text-danger">{err}</p>}

              <button
                type="button"
                onClick={submit}
                disabled={sending}
                className={`mt-4 w-full rounded-xl py-4 text-[16px] font-semibold leading-6 text-white transition ${!sending ? "cursor-pointer bg-primary hover:bg-primary-hover" : "cursor-not-allowed bg-[#A9B4CC]"}`}
              >
                {sending ? t("form.sending") : t("oneclick.leaveRequest")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
